import React from 'react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

const PromoCodeTable = ({
  promoCodes,
  loading,
  selectedPromoCodes,
  toggleSelectPromoCode,
  toggleSelectAll,
  openEditModal,
  openDeleteModal,
  sortField,
  sortDirection,
  handleSortChange,
  hasPermission
}) => {
  // Format dates
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return format(new Date(dateString), 'dd MMM yyyy HH:mm', { locale: fr });
  };

  // Format short date for mobile
  const formatShortDate = (dateString) => {
    if (!dateString) return '-';
    return format(new Date(dateString), 'dd/MM/yy', { locale: fr });
  };

  const getSortIcon = (field) => {
    if (sortField !== field) {
      return (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
        </svg>
      );
    }
    
    return sortDirection === 'asc' ? (
      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
      </svg>
    ) : (
      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
      </svg>
    );
  };

  if (loading) {
    return (
      <div className="w-full bg-white rounded-lg shadow-lg">
        <div className="flex justify-center items-center py-12 sm:py-20">
          <div className="flex flex-col items-center">
            <svg className="animate-spin h-8 w-8 sm:h-12 sm:w-12 text-blue-600 mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <p className="text-gray-500 text-sm sm:text-lg">Chargement des codes promo...</p>
          </div>
        </div>
      </div>
    );
  }

  if (promoCodes.length === 0) {
    return (
      <div className="w-full bg-white rounded-lg shadow-lg">
        <div className="flex justify-center items-center py-12 sm:py-20">
          <div className="flex flex-col items-center text-center px-4">
            <div className="p-3 sm:p-4 bg-gray-100 rounded-full mb-4 sm:mb-6">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 sm:h-12 sm:w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
              </svg>
            </div>
            <h3 className="text-lg sm:text-xl font-medium text-gray-900 mb-2 sm:mb-3">Aucun code promo trouvé</h3>
            <p className="text-sm sm:text-base text-gray-500">Commencez par créer votre premier code promotionnel</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full bg-white rounded-lg shadow-lg overflow-hidden">
      {/* Desktop Table - Hidden on mobile */}
      <div className="hidden lg:block overflow-x-auto">
        <table className="w-full divide-y divide-gray-200">
          <thead className="bg-gradient-to-r from-gray-50 to-gray-100">
            <tr>
              <th scope="col" className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider w-16">
                <input
                  type="checkbox"
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded transition-colors"
                  checked={selectedPromoCodes.length === promoCodes.length && promoCodes.length > 0}
                  onChange={toggleSelectAll}
                />
              </th>
              <th 
                scope="col" 
                className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-200 transition-colors"
                onClick={() => handleSortChange('code')}
              >
                <div className="flex items-center">
                  <span>Code</span>
                  <span className="ml-2">{getSortIcon('code')}</span>
                </div>
              </th>
              <th 
                scope="col" 
                className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-200 transition-colors"
                onClick={() => handleSortChange('montant')}
              >
                <div className="flex items-center">
                  <span>Montant</span>
                  <span className="ml-2">{getSortIcon('montant')}</span>
                </div>
              </th>
              <th 
                scope="col" 
                className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-200 transition-colors"
                onClick={() => handleSortChange('quantite')}
              >
                <div className="flex items-center">
                  <span>Quantité</span>
                  <span className="ml-2">{getSortIcon('quantite')}</span>
                </div>
              </th>
              <th 
                scope="col" 
                className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-200 transition-colors"
                onClick={() => handleSortChange('nbrUsed')}
              >
                <div className="flex items-center">
                  <span>Utilisé</span>
                  <span className="ml-2">{getSortIcon('nbrUsed')}</span>
                </div>
              </th>
              <th 
                scope="col" 
                className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-200 transition-colors"
                onClick={() => handleSortChange('totalTND')}
              >
                <div className="flex items-center">
                  <span>Total TND</span>
                  <span className="ml-2">{getSortIcon('totalTND')}</span>
                </div>
              </th>
              <th 
                scope="col" 
                className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-200 transition-colors"
                onClick={() => handleSortChange('updatedDatetime')}
              >
                <div className="flex items-center">
                  <span>Mis à jour</span>
                  <span className="ml-2">{getSortIcon('updatedDatetime')}</span>
                </div>
              </th>
              <th scope="col" className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                Statut
              </th>
              <th scope="col" className="relative px-6 py-4">
                <span className="sr-only">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-100">
            {promoCodes.map((promoCode) => {
              const totalTNDSaved = (promoCode.nbrUsed || 0) * (promoCode.montant || 0);
              
              return (
                <tr 
                  key={promoCode.id} 
                  className={`transition-all duration-200 ${
                    selectedPromoCodes.includes(promoCode.id) 
                      ? 'bg-blue-50 border-l-4 border-blue-500 shadow-sm' 
                      : 'hover:bg-gray-50'
                  }`}
                >
                  <td className="px-6 py-4 whitespace-nowrap">
                    <input
                      type="checkbox"
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded transition-colors"
                      checked={selectedPromoCodes.includes(promoCode.id)}
                      onChange={() => toggleSelectPromoCode(promoCode.id)}
                    />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0">
                        <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center">
                          <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                          </svg>
                        </div>
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-bold text-gray-900 font-mono">
                          {promoCode.code}
                        </div>
                        <div className="text-xs text-gray-500">
                          ID: {promoCode.id}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <span className="text-lg font-bold text-green-600">
                        {promoCode.montant}
                      </span>
                      <span className="ml-1 text-sm font-medium text-gray-500">TND</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">
                      {promoCode.quantite === 0 ? 'Illimité' : promoCode.quantite}
                    </div>
                    {promoCode.quantite > 0 && (
                      <div className="w-full bg-gray-200 rounded-full h-1.5 mt-1">
                        <div 
                          className="bg-blue-600 h-1.5 rounded-full transition-all duration-300" 
                          style={{ 
                            width: `${Math.min((promoCode.nbrUsed / promoCode.quantite) * 100, 100)}%` 
                          }}
                        ></div>
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">
                      {promoCode.nbrUsed || 0}
                      {promoCode.quantite > 0 && (
                        <span className="text-xs text-gray-500 ml-1">
                          / {promoCode.quantite}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <span className="text-lg font-bold text-orange-600">
                        {totalTNDSaved.toFixed(2)}
                      </span>
                      <span className="ml-1 text-sm font-medium text-gray-500">TND</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-xs text-gray-500">
                    {formatDate(promoCode.updatedDatetime)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
                      promoCode.active 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-red-100 text-red-800'
                    }`}>
                      <span className={`w-1.5 h-1.5 rounded-full mr-1.5 ${
                        promoCode.active ? 'bg-green-500' : 'bg-red-500'
                      }`}></span>
                      {promoCode.active ? 'Actif' : 'Inactif'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end space-x-2">
                      <button
                      disabled={!hasPermission('edit')}
                        onClick={() => openEditModal(promoCode)}
                        className="disabled:bg-gray-400 disabled:text-gray-700  disabled:cursor-not-allowed p-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg transition-colors"
                        title="Modifier"
                      >
                        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                      </button>
                      <button
                      disabled={!hasPermission('delete')}
                        onClick={() => openDeleteModal(promoCode)}
                        className="disabled:bg-gray-400 disabled:text-gray-700  disabled:cursor-not-allowed p-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-lg transition-colors"
                        title="Supprimer"
                      >
                        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Mobile/Tablet Card View - Visible on smaller screens */}
      <div className="lg:hidden">
        {/* Mobile Header with Select All */}
        <div className="bg-gradient-to-r from-gray-50 to-gray-100 px-4 py-3 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <input
                type="checkbox"
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded transition-colors"
                checked={selectedPromoCodes.length === promoCodes.length && promoCodes.length > 0}
                onChange={toggleSelectAll}
              />
              <span className="ml-3 text-sm font-medium text-gray-700">
                Tout sélectionner ({selectedPromoCodes.length})
              </span>
            </div>
            
            {/* Mobile Sort Dropdown */}
            <select
              value={`${sortField}-${sortDirection}`}
              onChange={(e) => {
                const [field, direction] = e.target.value.split('-');
                if (sortField !== field) {
                  handleSortChange(field);
                } else if (sortDirection !== direction) {
                  handleSortChange(field);
                }
              }}
              className="text-xs border border-gray-300 rounded-md px-2 py-1 bg-white"
            >
              <option value="code-asc">Code A-Z</option>
              <option value="code-desc">Code Z-A</option>
              <option value="montant-desc">Montant ↓</option>
              <option value="montant-asc">Montant ↑</option>
              <option value="nbrUsed-desc">Plus utilisé</option>
              <option value="nbrUsed-asc">Moins utilisé</option>
              <option value="updatedDatetime-desc">Plus récent</option>
              <option value="updatedDatetime-asc">Plus ancien</option>
            </select>
          </div>
        </div>

        {/* Mobile Cards */}
        <div className="divide-y divide-gray-200">
          {promoCodes.map((promoCode) => {
            const totalTNDSaved = (promoCode.nbrUsed || 0) * (promoCode.montant || 0);
            const usagePercentage = promoCode.quantite > 0 
              ? Math.round((promoCode.nbrUsed / promoCode.quantite) * 100) 
              : 0;
            
            return (
              <div 
                key={promoCode.id} 
                className={`p-4 transition-all duration-200 ${
                  selectedPromoCodes.includes(promoCode.id) 
                    ? 'bg-blue-50 border-l-4 border-blue-500' 
                    : 'bg-white hover:bg-gray-50'
                }`}
              >
                {/* Card Header */}
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center flex-1 min-w-0">
                    <input
                      type="checkbox"
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded transition-colors flex-shrink-0"
                      checked={selectedPromoCodes.includes(promoCode.id)}
                      onChange={() => toggleSelectPromoCode(promoCode.id)}
                    />
                    <div className="ml-3 flex-1 min-w-0">
                      <div className="flex items-center">
                        <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center flex-shrink-0">
                          <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                          </svg>
                        </div>
                        <div className="ml-3 min-w-0">
                          <div className="text-lg font-bold text-gray-900 font-mono truncate">
                            {promoCode.code}
                          </div>
                          <div className="text-xs text-gray-500">
                            ID: {promoCode.id}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Status Badge */}
                  <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ml-2 flex-shrink-0 ${
                    promoCode.active 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-red-100 text-red-800'
                  }`}>
                    <span className={`w-1.5 h-1.5 rounded-full mr-1.5 ${
                      promoCode.active ? 'bg-green-500' : 'bg-red-500'
                    }`}></span>
                    {promoCode.active ? 'Actif' : 'Inactif'}
                  </span>
                </div>

                {/* Main Content Grid */}
                <div className="grid grid-cols-2 gap-3 mb-3">
                  {/* Amount */}
                  <div className="bg-green-50 rounded-lg p-3">
                    <div className="text-xs text-green-600 font-medium mb-1">Montant</div>
                    <div className="flex items-baseline">
                      <span className="text-xl font-bold text-green-700">{promoCode.montant}</span>
                      <span className="text-sm text-green-600 ml-1">TND</span>
                    </div>
                  </div>

                  {/* Total Saved */}
                  <div className="bg-orange-50 rounded-lg p-3">
                    <div className="text-xs text-orange-600 font-medium mb-1">Total économisé</div>
                    <div className="flex items-baseline">
                      <span className="text-xl font-bold text-orange-700">{totalTNDSaved.toFixed(2)}</span>
                      <span className="text-sm text-orange-600 ml-1">TND</span>
                    </div>
                  </div>

                  {/* Quantity */}
                  <div className="bg-blue-50 rounded-lg p-3">
                    <div className="text-xs text-blue-600 font-medium mb-1">Quantité</div>
                    <div className="text-lg font-bold text-blue-700">
                      {promoCode.quantite === 0 ? 'Illimité' : promoCode.quantite}
                    </div>
                    {promoCode.quantite > 0 && (
                      <div className="w-full bg-blue-200 rounded-full h-1.5 mt-2">
                        <div 
                          className="bg-blue-600 h-1.5 rounded-full transition-all duration-300" 
                          style={{ width: `${Math.min((promoCode.nbrUsed / promoCode.quantite) * 100, 100)}%` }}
                        ></div>
                      </div>
                    )}
                  </div>

                  {/* Usage */}
                  <div className="bg-purple-50 rounded-lg p-3">
                    <div className="text-xs text-purple-600 font-medium mb-1">Utilisations</div>
                    <div className="text-lg font-bold text-purple-700">
                      {promoCode.nbrUsed || 0}
                      {promoCode.quantite > 0 && (
                        <span className="text-sm text-purple-600 font-normal ml-1">
                          / {promoCode.quantite}
                        </span>
                      )}
                    </div>
                    {promoCode.quantite > 0 && promoCode.nbrUsed > 0 && (
                      <div className="text-xs text-purple-600 mt-1">
                        {usagePercentage}% utilisé
                      </div>
                    )}
                  </div>
                </div>

                {/* Footer */}
                <div className="flex items-center justify-between pt-3 border-t border-gray-200">
                  <div className="flex items-center text-xs text-gray-500">
                    <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span className="hidden sm:inline">{formatDate(promoCode.updatedDatetime)}</span>
                    <span className="sm:hidden">{formatShortDate(promoCode.updatedDatetime)}</span>
                  </div>
                  
                  {/* Action Buttons */}
                  <div className="flex items-center space-x-2">
                    <button
                    disabled={!hasPermission('edit')}
                      onClick={() => openEditModal(promoCode)}
                      className="disabled:bg-gray-400 disabled:text-gray-700  disabled:cursor-not-allowed p-2 text-blue-600 hover:text-blue-800 hover:bg-blue-100 rounded-lg transition-colors"
                      title="Modifier"
                    >
                      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                    </button>
                    <button
                      onClick={() => openDeleteModal(promoCode)}
                      disabled={!hasPermission('delete')}

                      className="disabled:bg-gray-400 disabled:text-gray-700  disabled:cursor-not-allowed p-2 text-red-600 hover:text-red-800 hover:bg-red-100 rounded-lg transition-colors"
                      title="Supprimer"
                    >
                      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default PromoCodeTable;